
import React, { useState, useEffect } from 'react';
import { Promise } from 'bluebird';
import { DownloadOutlined, CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Image } from 'antd';
import withStyles from 'react-jss';
import axios from 'axios';
import { downloadFileData } from '../../utils/utils';

const styles = {
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContainer: {
    backgroundColor: '#fff',
    maxWidth: '90%',
    maxHeight: '90%',
    padding: '20px',
    borderRadius: '10px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    position: 'relative',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    margin: 0,
  },
  closeButton: {
    fontSize: '24px',
    cursor: 'pointer',
  },
  downloadButton: {
    marginRight: '10px',
    fontSize: '24px',
    color: '#1890ff',
    cursor: 'pointer',
  },
  downloadAllButton: {
    marginTop: '20px',
    textAlign: 'center',
    fontSize: '16px',
    color: '#1890ff',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
  },
  progressBars: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '20px',
    gap: '8px',
  },
  bar: {
    width: '30px',
    height: '6px',
    backgroundColor: '#d9d9d9',
    borderRadius: '4px',
    transition: 'all 0.3s ease',
  },
  activeBar: {
    width: '50px',
    height: '6px',
    backgroundColor: '#1890ff',
    borderRadius: '4px',
  },
  imageContainer: {
    position: 'relative',
    display: 'inline-block',
    maxHeight: '400px',
    maxWidth: '100%',
  },
  navigationArrow: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '32px',
    color: '#1890ff',
    cursor: 'pointer',
    zIndex: 10,
  },
  dummyImage: {
    width: 'auto',
    maxHeight: '350px',
    height: 'auto',
    border: '2px dashed #d9d9d9',
    borderRadius: '8px',
    backgroundColor: '#f5f5f5',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#8c8c8c',
    fontSize: '18px',
    textAlign: 'center',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  errorMessage: {
    marginTop: '20px',
    color: '#ff4d4f',
    textAlign: 'center',
    fontSize: '16px',
  },
};

const DocumentViewerModal = ({ isOpen, onClose, documents, classes }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  useEffect(() => {
    setIsImageLoaded(false);
    setDownloadError(null);
  }, [currentPage]);

  const handleNextPage = () => {
    if (currentPage < documents.length - 1) {
      setCurrentPage((prevPage) => prevPage + 1);
      setHasError(false);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prevPage) => prevPage - 1);
      setHasError(false);
    }
  };

  const handleClose = () => {
    setDownloadError(null);
    setHasError(false);
    onClose();
  };

  if (!isOpen || !documents?.length) return null;

  const currentDocumentTitle = documents[currentPage]?.name || 'Document';

  const handleDownloadCurrent = async () => {
    const currentDoc = documents[currentPage];
    if (!currentDoc?.url) {
      setDownloadError('Document URL not available for download.');
      return;
    }
  
    try {
      const response = await axios({
        method: 'get',
        url: currentDoc.url,
        responseType: 'blob',
      });
  
      const blob = response.data;
      const fileName = `${currentDoc.name || 'document'}_${currentDoc.cnNumber}`;
  
      downloadFileData(blob, fileName, '.jpeg');
  
      setDownloadError(null);
    } catch (error) {
      setDownloadError('Failed to download the file.');
    }
  };

  const handleDownloadAll = async () => {
    try {
      await Promise.map(documents, async (doc) => {
        if (doc?.url) {
            const response = await axios({
                method: 'get',
                url: doc.url,
                responseType: 'blob',
            });

            const blob = response.data;
            const fileName = `${doc.name || 'document'}_${doc.cnNumber}`;

            downloadFileData(blob, fileName, '.jpeg');
          }
    }, { concurrency: 1 });
  
      setDownloadError(null);
    } catch (error) {
      setDownloadError('Failed to download one or more files.');
    }
  };

  return (
    <div className={classes.modalOverlay}>
      <div className={classes.modalContainer}>
        <div className={classes.header}>
          <h2 className={classes.title}>{currentDocumentTitle}</h2>
          <div>
            <DownloadOutlined
              className={classes.downloadButton}
              onClick={handleDownloadCurrent}
              title="Download Document"
            />
            <CloseOutlined
              className={classes.closeButton}
              onClick={handleClose}
              title="Close"
            />
          </div>
        </div>

        {/* Navigation Arrows */}
        <div className={classes.imageContainer}>
          {/* Left Arrow */}
          {isImageLoaded && currentPage > 0 && (
            <LeftOutlined
              className={classes.navigationArrow}
              style={{ left: '5px' }}
              onClick={handlePreviousPage}
            />
          )}

          {/* Image */}
          {!hasError ? (
            <Image
              preview={true}
              src={documents[currentPage]?.url}
              alt="Document preview"
              style={{
                maxHeight: '400px',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
              onLoad={() => setIsImageLoaded(true)}
              onError={() => {
                setHasError(true);
                setIsImageLoaded(true);
              }}
            />
          ) : (
            <div className={classes.dummyImage}>Preview Not Available</div>
          )}

          {/* Right Arrow */}
          {isImageLoaded && currentPage < documents.length - 1 && (
            <RightOutlined
              className={classes.navigationArrow}
              style={{ right: '5px' }}
              onClick={handleNextPage}
            />
          )}

          {/* Error Message */}
          {downloadError && (
            <div className={classes.errorMessage}>
              <p>{downloadError}</p>
            </div>
          )}
        </div>

        {/* Progress Bars */}
        <div className={classes.progressBars}>
          {documents.map((_, index) => (
            <div
              key={index}
              className={`${classes.bar} ${currentPage === index ? classes.activeBar : ''
                }`}
              onClick={() => {
                setCurrentPage(index);
                setHasError(false);
              }}
            />
          ))}
        </div>

        {/* Download All Button */}
        <div
          className={classes.downloadAllButton}
          onClick={handleDownloadAll}
          title="Download All Documents"
        >
          <DownloadOutlined />
          Download All
        </div>
      </div>
    </div>
  );
};

export default withStyles(styles)(DocumentViewerModal);
